import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Zap, WifiOff, Share2, PlusSquare } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [showIOSGuide, setShowIOSGuide] = useState<boolean>(false);

  useEffect(() => {
    // 1. Vérifier si l'application est déjà lancée en tant que PWA installée
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches || 
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      return; // Ne rien afficher si l'app est déjà installée et ouverte
    }

    // 2. Vérifier si l'utilisateur a fermé le popup récemment (pendant cette session)
    const hasDismissed = sessionStorage.getItem('pwa_prompt_dismissed');
    if (hasDismissed) {
      return;
    }

    // 3. Détecter iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent) && !(window as any).MSStream;
    setIsIOS(isIosDevice);

    // 4. Écouter l'événement standard d'installation Chrome / Edge / Android
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 5. Afficher le joli pop-up automatiquement après 2.5 secondes
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Déclencheur natif Android / Chrome / Edge
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setIsOpen(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      // Sur iOS Safari, basculer sur le guide explicatif étape par étape
      setShowIOSGuide(true);
    } else {
      // Sur navigateurs de bureau sans déclencheur direct
      alert("Pour installer l'application, cliquez sur l'icône d'installation située à droite dans la barre d'adresse de votre navigateur (ou dans le menu ⋮ > Installer HopFitness).");
    }
  };

  const handleDismiss = () => {
    setIsOpen(false);
    setShowIOSGuide(false);
    // Masquer pour le reste de la navigation dans cette session
    sessionStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  if (!isOpen) return null;

  return (
    <aside aria-label="Installation de l'application" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-[390px] z-[9999] animate-fade-in">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#111827] via-[#0B0F19] to-black text-white p-5 rounded-2xl shadow-2xl border border-yellow-500/40 backdrop-blur-xl">
        
        {/* Glow décoratif en arrière plan */}
        <div className="absolute -top-12 -right-12 w-28 h-28 bg-yellow-500/15 rounded-full blur-2xl pointer-events-none" />

        {/* Bouton fermer */}
        <button
          onClick={handleDismiss}
          className="absolute top-3.5 right-3.5 text-gray-400 hover:text-white p-1 rounded-full transition-colors hover:bg-white/10"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        {/* En-tête avec Logo HopFitness */}
        <div className="flex items-center gap-3.5 mb-3.5">
          <div className="relative">
            <img 
              src="/logohop_fitness.jpeg" 
              alt="HopFitness" 
              className="w-13 h-13 rounded-xl object-cover border-2 border-yellow-400 shadow-lg shadow-yellow-500/10"
              style={{ width: '52px', height: '52px' }}
            />
            <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 border-2 border-[#0B0F19]"></span>
            </span>
          </div>

          <div>
            <div className="flex items-center gap-1.5">
              <h4 className="text-base font-extrabold text-white tracking-tight">HopFitness</h4>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-400/20 text-yellow-300 px-1.5 py-0.5 rounded border border-yellow-400/30">
                App
              </span>
            </div>
            <p className="text-xs text-gray-300">Application mobile officielle</p>
          </div>
        </div>

        {/* Corps ou Guide iOS */}
        {!showIOSGuide ? (
          <>
            <p className="text-xs text-gray-300 mb-3.5 leading-relaxed">
              Téléchargez l'application sur votre écran d'accueil pour une expérience optimale et rapide :
            </p>

            {/* Avantages clés */}
            <div className="grid grid-cols-2 gap-2 mb-4 text-[11px] text-gray-300">
              <div className="flex items-center gap-1.5 bg-white/5 p-2 rounded-lg border border-white/5">
                <Zap size={13} className="text-yellow-400 shrink-0" />
                <span>Accès en 1 clic</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/5 p-2 rounded-lg border border-white/5">
                <WifiOff size={13} className="text-yellow-400 shrink-0" />
                <span>Mode hors-ligne</span>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center gap-2.5">
              <button
                onClick={handleInstallClick}
                className="flex-1 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-gray-950 font-bold py-2.5 px-4 rounded-xl text-xs flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/20 transition-all transform active:scale-95"
              >
                <Download size={15} />
                {isIOS ? "Comment installer" : "Installer l'application"}
              </button>
              <button
                onClick={handleDismiss}
                className="text-xs text-gray-400 hover:text-white px-3 py-2.5 rounded-xl hover:bg-white/5 transition"
              >
                Plus tard
              </button>
            </div>
          </>
        ) : (
          /* Guide dédié iPhone / iPad (Safari) */
          <div className="space-y-2.5 bg-white/5 p-3 rounded-xl border border-yellow-500/30 text-xs">
            <h5 className="font-bold text-yellow-400 flex items-center gap-1.5">
              <Smartphone size={14} /> Installation sur iPhone :
            </h5>
            <ol className="list-decimal list-inside space-y-1.5 text-gray-200 text-[11.5px]">
              <li>
                Appuyez sur le bouton <strong className="text-white">Partager</strong> <Share2 size={12} className="inline text-yellow-400 mx-0.5" /> au bas de Safari.
              </li>
              <li>
                Faites défiler et choisissez <strong className="text-yellow-300">« Sur l'écran d'accueil »</strong> <PlusSquare size={12} className="inline text-yellow-400 mx-0.5" />.
              </li>
              <li>
                Appuyez sur <strong className="text-white">Ajouter</strong> en haut à droite.
              </li>
            </ol>
            <button
              onClick={handleDismiss}
              className="w-full mt-2 bg-yellow-400 text-gray-950 font-bold py-1.5 rounded-lg text-xs"
            >
              J'ai compris !
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default PWAInstallPrompt;
